<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import dayjs, { Dayjs } from "dayjs";
import { useApiClient } from "@/api/client";
import { useExplorerStore } from "@/modules/explorer/store";
import { DateRange, DateRangeParsed } from "@/ui/components/date/types";
import DateRangePickerWithPresets from "@/ui/components/date/DateRangePickerWithPresets.vue";
import Granularity from "@/modules/explorer/components/PeriodFilterSelector/Components/Granularity.vue";
import DayOfWeek from "@/modules/explorer/components/PeriodFilterSelector/Components/DayOfWeek.vue";
import {
	BaseDimensionState,
	FilterStateMachine,
	Granularity as GranularityType,
} from "@/modules/explorer/components/PeriodFilterSelector/StateMachine";
import DimensionDO = App.Data.DimensionDO;
import PeriodSelectionDTO = App.Data.Explorer.PeriodSelectionDTO;
import ClickhouseDateTimeGranularity = App.Enums.ClickhouseDateTimeGranularity;
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import { useBaseStore } from "@/stores/base";

const store = useExplorerStore();
const emits = defineEmits(["apply"]);
const periodSelection = defineModel<PeriodSelectionDTO>();
const activeTab = ref(0);
const activeGranularity = ref<GranularityType>("60min");
const showAdvanced = ref(false);
const dimensions = ref<DimensionDO[]>([]);

const isForMedia = computed(() => {
	return useBaseStore().currentEnvironment.product_plan.name_clean === "media";
});

const TABS = [
	{ value: "periodsubjectdate", label: "Subject", icon: "mdi-calendar-check" },
	{
		value: isForMedia.value
			? "periodcampaignstartdate"
			: "periodmovementindate",
		label: isForMedia.value ? "Campaign Start Date" : "Entry",
		icon: "mdi-calendar-arrow-right",
	},
	{
		value: isForMedia.value ? "periodcampaignenddate" : "periodmovementexdate",
		label: isForMedia.value ? "Campaign End Date" : "Exit",
		icon: "mdi-calendar-arrow-left",
	},
	{
		value: "periodreservationdate",
		label: "Reservation",
		icon: "mdi-calendar-text",
	},
];

const state = reactive<FilterStateMachine>(
	Object.fromEntries(TABS.map((tab) => [tab.value, { granularities: {} }]))
);

const dateRange = ref<DateRange>({
	from: periodSelection.value?.from ?? "now",
	to: periodSelection.value?.to ?? "now",
});

const dateRangeParsed = ref<DateRangeParsed>({
	from: dayjs(periodSelection.value?.from_parsed) ?? dayjs(),
	to: dayjs(periodSelection.value?.to_parsed) ?? dayjs(),
});

const currentBaseDimension = computed(() => TABS[activeTab.value]?.value);

/**
 * Only filter periods may be open-ended. The Subject tab edits the document's own
 * period_selection, which has to stay bounded, so it gets neither an open bound nor
 * the clear buttons that produce one.
 */
const isOpenEndedPeriodAllowed = computed(
	() => currentBaseDimension.value !== "periodsubjectdate"
);

const currentOptions = computed<GranularityType[]>(() => {
	const baseDim = currentBaseDimension.value;
	if (!baseDim || !state[baseDim]?.granularities) return [];
	return Object.keys(state[baseDim].granularities) as GranularityType[];
});

const showClearButton = computed(() => {
	if (activeTab.value !== 0) {
		return true;
	}

	const hasAnyGranules = Object.keys(
		state.periodsubjectdate.granularities
	).some(
		(granule) => state.periodsubjectdate.granularities[granule].length > 0
	);

	const hasAnyWeekDays =
		state.periodsubjectdate.dayofweek &&
		state.periodsubjectdate.dayofweek.length > 0;
	return hasAnyGranules || hasAnyWeekDays;
});

function updateActiveGranularity() {
	const options = currentOptions.value;

	if (options.length > 0 && !options.includes(activeGranularity.value)) {
		activeGranularity.value = options[0];
	} else if (options.length === 0) {
		activeGranularity.value = "60min";
	}
}

/**
 * An open bound has no parsed date, and `dayjs(null)` yields an Invalid Date — a bound
 * that reads as present but holds nothing. Absent stays absent, normalised to the `null`
 * sentinel the nullable pickers write.
 */
const cloneParsedBound = (value: Date | Dayjs | null | undefined) =>
	value ? dayjs(value) : null;

function cloneSelection(
	s: PeriodSelectionDTO | undefined
): PeriodSelectionDTO | undefined {
	if (!s) return undefined;

	return {
		from: s.from,
		to: s.to,
		from_parsed: cloneParsedBound(s.from_parsed),
		to_parsed: cloneParsedBound(s.to_parsed),
	};
}

function constructState(dimensions: DimensionDO[]) {
	dimensions.forEach((dimension) => {
		const baseDimension = dimension.family_name_clean;
		const granularity = dimension.granularity as ClickhouseDateTimeGranularity;

		if (!state[baseDimension] || !granularity) return;

		if (granularity === "day") {
			if (baseDimension === "periodsubjectdate") {
				state[baseDimension].day = cloneSelection(
					store.document.period_selection
				);
			} else {
				state[baseDimension].day = cloneSelection(
					store.GET_ACTIVE_FILTER(`${baseDimension}day`)?.values[0]
						?.filter_period ?? undefined
				);
			}
		} else if (granularity === "dayofweek") {
			state[baseDimension].dayofweek ??=
				store.GET_ACTIVE_FILTER(`${baseDimension}dayofweek`)?.values ?? [];
		} else {
			state[baseDimension].granularities ??= {};
			state[baseDimension].granularities[granularity as GranularityType] =
				store.GET_ACTIVE_FILTER_BY_GRANULARITY(baseDimension, granularity)
					?.values ?? [];
		}
	});

	// If we're opening it via a selected filter, then just check out where we need to go.
	if (store.explorerState.selectedFilter) {
		const filter = store.explorerState.selectedFilter as ActiveFilterDTO;
		const tabIndex = TABS.findIndex(
			(tab) => tab.value === filter.dimension.family_name_clean
		);

		if (tabIndex !== -1) {
			activeTab.value = tabIndex;
			showAdvanced.value = true;
			activeGranularity.value = filter.dimension.granularity as GranularityType;
		}
	}

	updateActiveGranularity();
}

function upsertFilter(filter: ActiveFilterDTO) {
	const exists = store.GET_ACTIVE_FILTER(filter.dimension.name_clean);
	if (filter.values.length > 0) {
		store.APPLY_FILTER(filter, exists ? "edit" : "create");
	} else if (exists && filter.values.length === 0) {
		// The filter is there, and the new one has nothing selected, yeet.
		const index = store.document.active_filters.findIndex(
			(f) => f.dimension.name_clean === filter.dimension.name_clean
		);

		if (index !== -1) {
			store.REMOVE_FILTER_BY_INDEX(index);
		}
	}
}

function buildFilterWithValues(
	baseDimension: string,
	granularity: ClickhouseDateTimeGranularity,
	values: FilterValueDTO[]
): ActiveFilterDTO {
	const dimensionObj = dimensions.value.find(
		(d) =>
			d.family_name_clean === baseDimension && d.granularity === granularity
	);

	if (!dimensionObj) {
		throw new Error(
			`Dimension not found: ${baseDimension} with granularity ${granularity}`
		);
	}

	return {
		dimension: dimensionObj,
		specification: null,
		values,
		valuesAreIncluded: true,
		isApplied: true,
		isDefault: false,
		isQuickFilter: false,
	};
}

function constructFilters() {
	const dim = currentBaseDimension.value;
	if (!dim || !state[dim]) return;

	if (dim === "periodsubjectdate") {
		if (!dateRange.value.from || !dateRange.value.to) return;

		state[dim].day = {
			from: dateRange.value.from,
			to: dateRange.value.to,
			from_parsed: dayjs(dateRangeParsed.value.from),
			to_parsed: dayjs(dateRangeParsed.value.to),
		};

		store.document.period_selection = cloneSelection(state[dim].day);
	} else if (dateRange.value.from || dateRange.value.to) {
		if (!state[dim].day) {
			state[dim].day = {
				from: dateRange.value.from,
				to: dateRange.value.to,
				from_parsed: cloneParsedBound(dateRangeParsed.value.from),
				to_parsed: cloneParsedBound(dateRangeParsed.value.to),
			};
		}
	} else {
		// Both bounds emptied: drop the period so the day filter is removed below.
		state[dim].day = undefined;
	}

	Object.keys(state).forEach((dimension) => {
		const dimensionalState = state[dimension] as BaseDimensionState | undefined;
		if (!dimensionalState) return;

		if (dimension !== "periodsubjectdate") {
			// A period with neither bound set is an emptied range, so it filters nothing
			// and its value list stays empty.
			const day = dimensionalState.day;
			const dayValues: FilterValueDTO[] =
				day?.from || day?.to
					? [
							{
								id: 1,
								level: null,
								selection_depth: null,
								model: null,
								label: "n/a",
								filter_period: cloneSelection(day),
							},
					  ]
					: [];

			if (
				dayValues.length > 0 ||
				store.GET_ACTIVE_FILTER_BY_GRANULARITY(dimension, "day")
			) {
				upsertFilter(buildFilterWithValues(dimension, "day", dayValues));
			}
		}

		if (dimensionalState.dayofweek) {
			upsertFilter(
				buildFilterWithValues(
					dimension,
					"dayofweek",
					dimensionalState.dayofweek
				)
			);
		}

		const granules = Object.entries(dimensionalState.granularities);
		for (const [granularity, values] of granules) {
			upsertFilter(
				buildFilterWithValues(
					dimension,
					granularity as ClickhouseDateTimeGranularity,
					values
				)
			);
		}
	});

	showAdvanced.value = false;
	emits("apply");
}

function clearFilters() {
	const dim = currentBaseDimension.value;
	if (!dim || !state[dim]) return;

	store.document.active_filters = store.document.active_filters.filter(
		(f) => f.dimension.family_name_clean !== dim
	);

	if (dim === "periodsubjectdate") {
		if (!state[dim].day) {
			const today = dayjs();
			state[dim].day = {
				from: "now",
				to: "now",
				from_parsed: today,
				to_parsed: today,
			};
		}
	} else {
		state[dim].day = undefined;
		dateRange.value = { from: undefined, to: undefined };
		dateRangeParsed.value = { from: undefined, to: undefined };
	}

	if (state[dim].dayofweek) {
		state[dim].dayofweek = [];
	}

	if (state[dim].granularities) {
		Object.keys(state[dim].granularities).forEach((key) => {
			state[dim].granularities[key] = [];
		});
	}

	showAdvanced.value = false;
}

async function initialize() {
	const response = await useApiClient("explorer").getDimensions({
		"filter[type]": "datetime",
		"filter[is_filterable]": true,
	});

	if (response.isOk()) {
		dimensions.value = response.value;
		constructState(dimensions.value);
	}
}

watch([activeTab, currentOptions], () => updateActiveGranularity());

watch(
	activeTab,
	() => {
		const dim = currentBaseDimension.value;
		if (!dim || !state[dim]) return;

		if (state[dim].day) {
			dateRange.value = {
				from: state[dim].day.from,
				to: state[dim].day.to,
			};
			dateRangeParsed.value = {
				from: cloneParsedBound(state[dim].day.from_parsed),
				to: cloneParsedBound(state[dim].day.to_parsed),
			};
		} else if (dim === "periodsubjectdate") {
			state[dim].day = periodSelection.value;
		} else {
			dateRange.value = { from: undefined, to: undefined };
			dateRangeParsed.value = { from: undefined, to: undefined };
		}
	},
	{ immediate: true }
);

watch(
	dateRange,
	() => {
		const dim = currentBaseDimension.value;
		if (!dim || !state[dim]) return;
		if (dim === "periodsubjectdate") return;

		if (state[dim].day) {
			state[dim].day.from = dateRange.value.from;
			state[dim].day.to = dateRange.value.to;
		} else if (dateRange.value.from || dateRange.value.to) {
			state[dim].day = {
				from: dateRange.value.from,
				to: dateRange.value.to,
				from_parsed: dateRangeParsed.value.from,
				to_parsed: dateRangeParsed.value.to,
			};
		}
	},
	{ deep: true }
);

watch(
	dateRangeParsed,
	(newParsed) => {
		const dim = currentBaseDimension.value;
		if (!dim || !state[dim]) return;
		if (dim === "periodsubjectdate") return;

		if (state[dim].day) {
			state[dim].day.from_parsed = newParsed.from
				? dayjs(newParsed.from)
				: newParsed.from;
			state[dim].day.to_parsed = newParsed.to
				? dayjs(newParsed.to)
				: newParsed.to;
		}
	},
	{ deep: true }
);

onMounted(initialize);
onUnmounted(() => {
	store.explorerState.selectedFilter = undefined;
	showAdvanced.value = false;
});
</script>

<template>
	<div class="period-filter-container">
		<v-slide-x-transition>
			<v-card
				v-if="showAdvanced"
				class="advanced-options-panel"
				min-width="450"
			>
				<div
					class="panel-header d-flex align-center px-4 py-2 pt-3"
					style="background-color: rgb(var(--color-secondary))"
				>
					<span class="text-subtitle-1">Additional Filters</span>
					<v-spacer></v-spacer>
					<v-btn
						icon="mdi-close"
						variant="text"
						density="comfortable"
						size="small"
						@click="showAdvanced = false"
					></v-btn>
				</div>
				<v-divider />

				<DayOfWeek v-model="state[currentBaseDimension].dayofweek" />
				<v-divider />
				<Granularity
					v-if="currentOptions.length > 0"
					v-model:granularity="activeGranularity"
					v-model="state[currentBaseDimension].granularities[activeGranularity]"
					:options="currentOptions"
				/>
			</v-card>
		</v-slide-x-transition>

		<v-card class="main-card">
			<div class="d-flex align-center border-b">
				<v-btn
					:active="showAdvanced"
					variant="flat"
					color="secondary"
					rounded="0"
					@click="showAdvanced = !showAdvanced"
					class="toggle-button"
					size="48"
				>
					<v-icon icon="fa-regular fa-filter" />
				</v-btn>

				<v-tabs
					v-model="activeTab"
					bg-color="secondary"
					show-arrows
					slider-color="primary"
					class="flex-grow-1"
				>
					<v-tab v-for="(item, index) in TABS" :key="index" :value="index">
						<v-icon :icon="item.icon" class="mr-2" />
						<span> {{ $t(`periodSelection.${item.value}`) }}</span>
					</v-tab>
				</v-tabs>
			</div>

			<DateRangePickerWithPresets
				show-presets
				:nullable="isOpenEndedPeriodAllowed"
				v-model="dateRange"
				v-model:parsed="dateRangeParsed"
			>
				<template #button="{ disabled }">
					<div class="d-flex ga-2">
						<v-btn
							class="flex-grow-1"
							variant="tonal"
							color="error"
							v-if="showClearButton"
							@click="clearFilters"
						>
							Clear
						</v-btn>
						<v-btn
							:disabled="disabled"
							variant="flat"
							class="flex-grow-1"
							color="secondary"
							@click="constructFilters"
						>
							Apply
						</v-btn>
					</div>
				</template>
			</DateRangePickerWithPresets>
		</v-card>
	</div>
</template>

<style scoped lang="scss">
.period-filter-container {
	position: relative;
	display: flex;
	gap: 16px;
	align-items: flex-start;
}

.main-card {
	flex: 1;
	z-index: 1;
}

.advanced-options-panel {
	position: relative;
	z-index: 1;
	max-width: 350px;
	overflow: hidden;
}

.toggle-button {
	min-width: 36px;
	min-height: 36px;
	border-radius: 0;
}

@media (max-width: 768px) {
	.period-filter-container {
		flex-direction: column;
	}

	.advanced-options-panel {
		width: 100%;
		max-width: none;
	}
}
</style>
